/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';

import type { AppThunk } from '../store';
import type { CncNavigatePayload } from '../store/slices/ui/uiActions';

import { useNavigationTracking } from '../../cnc/hooks/tracking/useCncNavigationTracking';
import { storeSelectors } from '../selectors/storeSelectors';
import { uiSelectors } from '../selectors/uiSelectors';
import { cncUiActions } from '../store/slices/ui/cncUiSlice';
import { unsyncedCartItemsActions } from '../store/slices/unsyncedCartItemsSlice';
import { FlyInType } from '../store/structureDefinitions/modalState';
import { CncStep } from '../store/structureDefinitions/uiState';
import { useTopLevelModal } from './useModal';
import { useAppDispatch, useAppSelector } from './useThunkDispatch';

export const dispatchNavigateTo =
    (arg: CncNavigatePayload): AppThunk =>
    (dispatch, getState) => {
        const hasActiveStoreSelection = storeSelectors.hasActiveStoreSelection(getState());
        if (
            hasActiveStoreSelection ||
            // the following steps can be accessed without a store selection:
            arg.step === CncStep.Map ||
            (arg.step === CncStep.PostalCodePreview && arg.param?.previewPostalCode)
        ) {
            dispatch(cncUiActions.navigateTo(arg));
        } else {
            dispatch(
                cncUiActions.navigateTo({
                    step: CncStep.PostalCodeSelector,
                }),
            );
        }
    };

export const dispatchSetTo =
    (arg: CncNavigatePayload): AppThunk =>
    (dispatch, getState) => {
        const hasActiveStoreSelection = storeSelectors.hasActiveStoreSelection(getState());
        if (!hasActiveStoreSelection) {
            dispatch(
                cncUiActions.setTo({
                    step: CncStep.PostalCodeSelector,
                }),
            );
            return;
        }
        dispatch(cncUiActions.setTo(arg));
    };

export const useCncFlyInState = () => {
    const dispatch = useAppDispatch();
    const { sendGaCloseTrackingEvent } = useNavigationTracking();

    const reloadOnClose = useAppSelector((state) => state.ui.cnc.reloadOnClose);
    const currentStep = useAppSelector(uiSelectors.cnc.selectCurrentStep);

    const { isOpen, close } = useTopLevelModal(FlyInType.CncFlyIn);

    const afterClose = useCallback(() => {
        sendGaCloseTrackingEvent();
        dispatch(unsyncedCartItemsActions.removeUnsyncedCartItems());
        if (reloadOnClose) {
            window.location.reload();
        }
    }, [dispatch, reloadOnClose, sendGaCloseTrackingEvent]);

    const navigateTo = useCallback(
        (args: CncNavigatePayload) => {
            dispatch(dispatchNavigateTo(args));
        },
        [dispatch],
    );

    const setTo = useCallback(
        (args: CncNavigatePayload) => {
            dispatch(dispatchSetTo(args));
        },
        [dispatch],
    );

    const initCnc = useCallback(() => {
        dispatch(
            dispatchSetTo({
                step: CncStep.MiniCart,
            }),
        );
    }, [dispatch]);

    const forceReloadOnClose = useCallback(() => {
        dispatch(cncUiActions.forceReloadOnClose(true));
    }, [dispatch]);

    return {
        isOpen,
        close,
        afterClose,
        navigateTo,
        setTo,
        initCnc,
        forceReloadOnClose,
        currentStep,
    };
};
